/**
 * A loop's step durations, learned from its finished runs' events, for the run page's
 * forecast (`./forecast.ts`).
 *
 * @since 1.0.0
 */
import { Match, Option, Schema } from "effect";
import type { EventOf } from "./derive/timeline.ts";
import { ofKind } from "./derive/timeline.ts";
import { DEFAULT_PROFILE, type DurationsProfile, type DurationStat } from "./forecast.ts";
import type { ConditionSpec, RunEvent } from "./model.ts";

/**
 * One finished past run of the loop, as its events.
 *
 * @since 1.0.0
 */
export interface ProfileSample {
  readonly events: ReadonlyArray<RunEvent>;
  /** The conditions of the loop version this run ran, so condition ids map to kinds. */
  readonly conditions: ReadonlyArray<ConditionSpec>;
}

/** One step's duration seen in a past run: its profile key and ms. */
type Observation = readonly [key: string, ms: number];

type Check = EventOf<"condition.checked">;
type ConditionEvent = EventOf<"condition.started"> | Check;

/** A repo-check part's evidence value (`check.<name>`, from the host's `checksResult`). */
const PartValue = Schema.Struct({
  ms: Schema.Number,
  reused: Schema.optional(Schema.Unknown),
  environment: Schema.optional(Schema.Unknown),
});
const decodePart = Schema.decodeUnknownOption(PartValue);

const PART_FIELD = "check.";

const timeOf = (event: { readonly at: string }): number => Date.parse(event.at);

const observe = (key: string, ms: number): Observation => [key, Math.max(0, ms)];

/** A pooled step: its own key (`<pool>:<name>`) and the pool's. */
const pooled = (pool: string, name: string, ms: number): ReadonlyArray<Observation> => [
  observe(`${pool}:${name}`, ms),
  observe(pool, ms),
];

const subjectOf = (event: ConditionEvent): string =>
  event.kind === "condition.started" ? event.subject : event.result.subject;

const isOpen = (action: EventOf<"action.taken">): boolean => action.ok && action.action === "change_request.open";

/** From `run.started` to the orchestrator's start. */
const setupOf = (events: ReadonlyArray<RunEvent>): ReadonlyArray<Observation> => {
  const started = ofKind(events, "run.started").at(0);
  const orchestrator = ofKind(events, "agent.started").find((e) => e.role === "orchestrator");
  return started === undefined || orchestrator === undefined
    ? []
    : [observe("setup", timeOf(orchestrator) - timeOf(started))];
};

/**
 * Each finished subagent's ms: a build when it started before the first check, else a fix. A
 * resumed run's first subagent continues kept work, so it is a fix.
 */
const subagentsOf = (events: ReadonlyArray<RunEvent>): ReadonlyArray<Observation> => {
  const firstCheck = ofKind(events, "condition.checked").at(0)?.seq ?? Number.POSITIVE_INFINITY;
  const resumed = ofKind(events, "run.started").at(0)?.resumedFrom !== undefined;
  const finished = new Map(ofKind(events, "agent.finished").map((e): readonly [string, number] => [e.agentId, e.ms]));
  return ofKind(events, "agent.started")
    .filter((e) => e.role === "subagent")
    .flatMap((e, i): ReadonlyArray<Observation> => {
      const ms = finished.get(e.agentId);
      return ms === undefined ? [] : [observe((resumed && i === 0) || e.seq > firstCheck ? "fix" : "build", ms)];
    });
};

/** A repo check's fresh parts, failing ones included: neither reused nor stopped by the host. */
const partsOf = (check: Check): ReadonlyArray<Observation> =>
  check.result.evidence
    .filter((evidence) => evidence.field.startsWith(PART_FIELD))
    .flatMap((evidence) =>
      decodePart(evidence.value).pipe(
        Option.filter((value) => value.reused === undefined && value.environment === undefined),
        Option.match({
          onNone: (): ReadonlyArray<Observation> => [],
          onSome: (value) => pooled("part", evidence.field.slice(PART_FIELD.length), value.ms),
        }),
      )
    );

/** A check's bar observations, unless it is pending or reused. */
const barOf = (check: Check, observations: ReadonlyArray<Observation>): ReadonlyArray<Observation> =>
  check.result.state === "pending" || check.result.reused === true ? [] : observations;

const checkOf = (kind: string, check: Check, barMs: number): ReadonlyArray<Observation> =>
  Match.value(kind).pipe(
    Match.when("workspace.checks", () => partsOf(check)),
    Match.when("workspace.no_conflicts", () => barOf(check, [observe("merge", barMs)])),
    Match.when("change_request.mergeable", () => barOf(check, [observe("mergeable", barMs)])),
    Match.when((k: string) => k.startsWith("review."), (k) => barOf(check, pooled("review", k, barMs))),
    Match.orElse((): ReadonlyArray<Observation> => []),
  );

/**
 * The checks' steps. A `condition.started` opens a bar for its (condition, subject); the next
 * check of that pair closes it, else the check spans its own `result.ms`.
 */
const checksOf = (
  events: ReadonlyArray<RunEvent>,
  kindOf: (conditionId: string) => string | undefined,
): ReadonlyArray<Observation> => {
  const open = new Map<string, number>();
  const barKey = (conditionId: string, subject: string) => JSON.stringify([conditionId, subject]);
  return events.flatMap((e): ReadonlyArray<Observation> => {
    if (e.kind === "condition.started") {
      open.set(barKey(e.conditionId, e.subject), timeOf(e));
      return [];
    }
    if (e.kind !== "condition.checked") {
      return [];
    }
    const key = barKey(e.conditionId, e.result.subject);
    const startMs = open.get(key);
    open.delete(key);
    const kind = kindOf(e.conditionId);
    return kind === undefined
      ? []
      : checkOf(kind, e, startMs === undefined ? e.result.ms : timeOf(e) - startMs);
  });
};

/**
 * CI per subject, to its first met check. It starts when the change request opened, if that
 * was the last open before the first ci start and on this subject's watch; else at the first
 * ci start; else the met check's own `result.ms` before it.
 */
const ciOf = (
  events: ReadonlyArray<RunEvent>,
  kindOf: (conditionId: string) => string | undefined,
): ReadonlyArray<Observation> => {
  const isCi = (conditionId: string) => kindOf(conditionId) === "change_request.ci_green";
  const conditionEvents = events.filter((e): e is ConditionEvent =>
    e.kind === "condition.started" || e.kind === "condition.checked"
  );
  const opens = ofKind(events, "action.taken").filter(isOpen);
  const met = ofKind(events, "condition.checked").filter((e) => isCi(e.conditionId) && e.result.state === "met");
  const firstMet = new Map([...met].reverse().map((e): readonly [string, Check] => [e.result.subject, e]));
  const startOf = (check: Check): number => {
    const subject = check.result.subject;
    const ciStart = ofKind(events, "condition.started").find((e) => e.subject === subject && isCi(e.conditionId));
    if (ciStart === undefined) {
      return timeOf(check) - check.result.ms;
    }
    const firstSeq = Math.min(...conditionEvents.filter((e) => subjectOf(e) === subject).map((e) => e.seq));
    const opened = opens.filter((e) => e.seq <= ciStart.seq).at(-1);
    return opened !== undefined && opened.seq >= firstSeq ? timeOf(opened) : timeOf(ciStart);
  };
  return [...firstMet.values()].map((check) => observe("ci", timeOf(check) - startOf(check)));
};

const observationsOf = (sample: ProfileSample): ReadonlyArray<Observation> => {
  const events = [...sample.events].sort((a, b) => a.seq - b.seq);
  const kinds = new Map(sample.conditions.map((c): readonly [string, string] => [c.id, c.kind]));
  const kindOf = (conditionId: string) => kinds.get(conditionId);
  return [
    ...setupOf(events),
    ...subagentsOf(events),
    ...checksOf(events, kindOf),
    ...ofKind(events, "action.taken").filter(isOpen).map((e) => observe("pr.open", e.ms)),
    ...ciOf(events, kindOf),
  ];
};

/** p50 and p90 (nearest rank) of at least one value. */
const statOf = (values: ReadonlyArray<number>): DurationStat => {
  const sorted = [...values].sort((a, b) => a - b);
  const rank = (pct: number) => sorted[Math.ceil((pct * sorted.length) / 100) - 1];
  return { p50Ms: rank(50), p90Ms: rank(90), samples: sorted.length };
};

/**
 * Step durations learned from past runs: each step's p50 and p90 (nearest rank) over every
 * time a past run took it, else its {@link DEFAULT_PROFILE} default. `part` and `review` pool
 * every part and review. `runs` counts the samples that finished; a truncated sample still
 * gives the steps it holds.
 *
 * @since 1.0.0
 */
export const profileFromRuns = (samples: ReadonlyArray<ProfileSample>): DurationsProfile => {
  const byKey = samples
    .flatMap(observationsOf)
    .reduce(
      (acc, [key, ms]) => acc.set(key, [...(acc.get(key) ?? []), ms]),
      new Map<string, ReadonlyArray<number>>(),
    );
  return {
    runs: samples.filter((sample) => sample.events.some((e) => e.kind === "run.finished")).length,
    steps: {
      ...DEFAULT_PROFILE.steps,
      ...Object.fromEntries([...byKey].map(([key, values]) => [key, statOf(values)])),
    },
  };
};
